import { Link } from '@tanstack/react-router'
import { ExternalLink, FileCheck, Calendar, Receipt } from 'lucide-react'

import { cn } from '@/lib/utils'

import type { PendingApproval } from '@/hooks'

interface PendingApprovalsWidgetProps {
  approvals: PendingApproval[] | null
  isLoading?: boolean
}

function formatCurrency(cents: number) {
  return `¥${(cents / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) {return '刚刚'}
  if (diffMins < 60) {return `${diffMins}分钟前`}
  if (diffHours < 24) {return `${diffHours}小时前`}
  if (diffDays < 7) {return `${diffDays}天前`}
  return date.toLocaleDateString('zh-CN')
}

function ApprovalItem({ approval }: { approval: PendingApproval }) {
  const isLeave = approval.type === 'leave'
  const Icon = isLeave ? Calendar : Receipt

  return (
    <a
      href={approval.detailUrl}
      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
    >
      <div className="mt-0.5">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{approval.applicantName}</span>
          <span
            className={cn(
              'text-xs px-2 py-0.5 rounded-full',
              isLeave ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
            )}
          >
            {isLeave ? '请假' : '报销'}
          </span>
        </div>
        <div className="text-sm text-muted-foreground mt-1">
          {isLeave ? (
            <span>{approval.leaveType} · {approval.days}天</span>
          ) : (
            <span>{approval.category} · {formatCurrency(approval.amount ?? 0)}</span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {formatRelativeTime(approval.submittedAt)}
        </div>
      </div>
    </a>
  )
}

function ApprovalItemSkeleton() {
  return (
    <div className="flex items-start gap-3 p-3 animate-pulse">
      <div className="h-4 w-4 bg-muted rounded mt-0.5" />
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <div className="h-4 w-20 bg-muted rounded" />
          <div className="h-5 w-12 bg-muted rounded-full" />
        </div>
        <div className="h-4 w-32 bg-muted rounded" />
        <div className="h-3 w-16 bg-muted rounded" />
      </div>
    </div>
  )
}

export function PendingApprovalsWidget({ approvals, isLoading }: PendingApprovalsWidgetProps) {
  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between p-6 pb-4">
        <h3 className="flex items-center gap-2 font-semibold">
          <FileCheck className="h-5 w-5" />
          待审批
        </h3>
        <Link
          to="/hr/leaves"
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          查看全部 <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
      <div className="px-3 pb-3">
        {isLoading ? (
          <>
            <ApprovalItemSkeleton />
            <ApprovalItemSkeleton />
            <ApprovalItemSkeleton />
          </>
        ) : !approvals || approvals.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            暂无待审批项
          </div>
        ) : (
          approvals.map((approval) => (
            <ApprovalItem key={approval.id} approval={approval} />
          ))
        )}
      </div>
    </div>
  )
}
